import { supabase } from './supabase';

const API_BASE = import.meta.env.VITE_API_URL || '';
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `API error: ${res.status}`);
  }
  return res.json();
}

async function getAccessToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');
  return session.access_token;
}

async function edgeFn<T>(fnName: string, path: string, options?: RequestInit): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${fnName}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'apikey': SUPABASE_ANON_KEY,
      ...options?.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `API error: ${res.status}`);
  }
  return res.json();
}

async function apiWithKey<T>(path: string, apiKey: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-Bonespay-Key': apiKey,
      ...options?.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `API error: ${res.status}`);
  }
  return res.json();
}

export const api = {
  health: () => request<{ status: string }>('/api/health'),

  convert: (amount: number, from: string) =>
    request<{ usd_amount: number; rate: number }>(
      `/api/currency/convert?amount=${amount}&from=${from}`
    ),

  generateWallet: (body: Record<string, unknown>) =>
    request<{ order_id: string; deposit_address: string; checkout_url: string }>(
      '/api/checkout/create',
      { method: 'POST', body: JSON.stringify(body) }
    ),

  getOrder: (uuid: string) =>
    request<Record<string, unknown>>(`/api/orders/${uuid}`),

  getOrderStatus: (uuid: string) =>
    request<{ status: string }>(`/api/orders/${uuid}/status`),

  verify: (hash: string) =>
    request<Record<string, unknown>>(`/api/verify/${hash}`),

  stripeCreateAccount: (apiKey: string) =>
    apiWithKey<{ success: boolean; stripe_account_id: string }>(
      '/api/stripe/accounts',
      apiKey,
      { method: 'POST' }
    ),

  stripeGetOnboardingUrl: (apiKey: string) =>
    apiWithKey<{ success: boolean; onboarding_url: string }>(
      '/api/stripe/accounts/onboard',
      apiKey,
      { method: 'POST' }
    ),

  stripeBalance: (apiKey: string) =>
    apiWithKey<Record<string, unknown>>('/api/stripe/balance', apiKey),

  stripePayouts: (apiKey: string, params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiWithKey<Record<string, unknown>>(`/api/stripe/payouts${qs}`, apiKey);
  },

  stripeTransfers: (apiKey: string, params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiWithKey<Record<string, unknown>>(`/api/stripe/transfers${qs}`, apiKey);
  },

  getStripePublishableKey: async () => {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/get-stripe-publishable-key`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error('Failed to fetch Stripe publishable key');
    return res.json() as Promise<{ publishable_key: string }>;
  },

  stripeConnectStatus: () =>
    edgeFn<{
      connected: boolean;
      stripe_account_id: string | null;
      charges_enabled: boolean;
      payouts_enabled: boolean;
      details_submitted: boolean;
    }>('stripe-connect-onboarding', '/status', { method: 'GET' }),

  stripeConnectOnboard: (returnUrl: string, refreshUrl: string) =>
    edgeFn<{ success: boolean; onboarding_url: string; stripe_account_id: string }>(
      'stripe-connect-onboarding',
      '',
      { method: 'POST', body: JSON.stringify({ return_url: returnUrl, refresh_url: refreshUrl }) },
    ),

  setSolanaWallet: (apiKey: string, body: { wallet_address: string }) =>
    apiWithKey<{ success: boolean }>('/api/merchant/solana-wallet', apiKey, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  getWalletBalance: (apiKey: string) =>
    apiWithKey<{
      usdc_balance: number;
      sol_balance: number;
      wallet_address: string;
      configured: boolean;
      network: string;
    }>('/api/merchant/wallet-balance', apiKey),

  getWalletTransactions: (apiKey: string, params?: { limit?: number; before?: string }) => {
    const entries = params
      ? Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
      : [];
    const qs = entries.length ? '?' + new URLSearchParams(entries as [string, string][]).toString() : '';
    return apiWithKey<{
      transactions: Array<{ signature: string; blockTime?: number; solscan_url?: string }>;
    }>(`/api/merchant/wallet-transactions${qs}`, apiKey);
  },

  edgeFn,
};
